import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, History, X } from "lucide-react";
import API from "../services/api";
import BottomNav from "../components/BottomNav";

function CasinoPage() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [loading, setLoading] = useState(null); // null | "casino" | "reel"
  const [lobbyURL, setLobbyURL] = useState(null);
  const [error, setError] = useState("");

  const launchGame = async (type) => {
    if (!user?.id) { navigate("/login"); return; }
    setLoading(type);
    setError("");
    const endpoint = type === "reel" ? "/api/reelgames/launch" : "/api/casino/launch";
    try {
      const res = await API.post(endpoint, { user_id: user.id });
      setLobbyURL(res.data.lobbyURL);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to launch game. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const closeGame = () => { setLobbyURL(null); navigate("/dashboard"); };

  if (lobbyURL) {
    return (
      <div style={iframeWrap}>
        <div style={iframeBar}>
          <span style={iframeTitle}>🎰 CASINO</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" style={{ ...iframeClose, background: "rgba(74,222,128,0.12)", borderRadius: 8 }} onClick={() => { setLobbyURL(null); navigate("/casino-bets"); }}>
              <History size={18} color="#4ade80" />
            </button>
            <button type="button" style={iframeClose} onClick={closeGame}>
              <X size={20} color="#fff" />
            </button>
          </div>
        </div>
        <iframe
          src={lobbyURL}
          title="Casino Game"
          style={iframeEl}
          allow="autoplay; fullscreen; payment; clipboard-write; microphone; camera"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <div style={page}>
      <header style={hdr}>
        <button type="button" style={backBtn} onClick={() => navigate("/dashboard")}>
          <ChevronLeft size={22} color="#fff" />
        </button>
        <span style={hdrTitle}>🎰 CASINO</span>
        <button type="button" style={histBtn} onClick={() => navigate("/casino-bets")}>
          <History size={18} color="#4ade80" />
        </button>
      </header>

      <main style={mainWrap}>
        <div style={heroCard}>
          <div style={heroGlow} />
          <div style={heroContent}>
            <div style={casinoIcon}>🎰</div>
            <h1 style={heroH1}>LIVE CASINO</h1>
            <p style={heroSub}>Play live table games with real dealers.<br />Roulette, Blackjack, Baccarat & more!</p>

            <div style={featureRow}>
              {["Live Dealers", "Real Money", "Instant Play"].map((f) => (
                <div key={f} style={featureChip}>{f}</div>
              ))}
            </div>

            {!!error && <div style={errorBox}>{error}</div>}

            <button
              type="button"
              style={loading ? launchBtnDisabled : launchBtn}
              onClick={() => launchGame("casino")}
              disabled={!!loading}
            >
              {loading === "casino" ? "Launching..." : "🎰 LIVE CASINO (Supernova)"}
            </button>

            <button
              type="button"
              style={loading ? reelBtnDisabled : reelBtn}
              onClick={() => launchGame("reel")}
              disabled={!!loading}
            >
              {loading === "reel" ? "Launching..." : "🎮 REEL GAMES"}
            </button>

            <p style={disclaimer}>18+ Only. This game involves real money. Please play responsibly.</p>

            <button type="button" style={historyLinkBtn} onClick={() => navigate("/casino-bets")}>
              <History size={14} /> View Bet History
            </button>
          </div>
        </div>
      </main>

      <BottomNav />
    </div>
  );
}

const DARK  = "#0a0f0a";
const CARD  = "#0f1a0f";
const GREEN = "#16a34a";
const BORDER= "rgba(255,255,255,0.08)";

const page     = { minHeight: "100vh", background: DARK, fontFamily: "Inter, system-ui, sans-serif", color: "#fff", paddingBottom: 90 };
const hdr      = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", height: 54, background: "#0a140a", borderBottom: `1px solid ${BORDER}`, position: "sticky", top: 0, zIndex: 50 };
const backBtn  = { border: 0, background: "transparent", cursor: "pointer", padding: 4, display: "grid", placeItems: "center" };
const hdrTitle = { fontSize: 18, fontWeight: 900, letterSpacing: "0.1em", color: "#fff" };
const histBtn  = { border: 0, background: "rgba(255,255,255,0.08)", borderRadius: 10, width: 38, height: 38, display: "grid", placeItems: "center", cursor: "pointer" };
const historyLinkBtn = { display: "inline-flex", alignItems: "center", gap: 6, height: 36, padding: "0 16px", border: "1px solid rgba(74,222,128,0.3)", borderRadius: 10, background: "rgba(74,222,128,0.08)", color: "#4ade80", fontSize: 13, fontWeight: 700, cursor: "pointer", marginTop: 4 };
const mainWrap = { padding: "16px 14px", maxWidth: 480, margin: "0 auto" };

const heroCard    = { borderRadius: 20, border: "1px solid rgba(22,163,74,0.4)", background: "linear-gradient(160deg, #0a140a 0%, #0f3a0f 70%, #14532d 100%)", overflow: "hidden", position: "relative" };
const heroGlow    = { position: "absolute", top: -80, right: -80, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(22,163,74,0.2) 0%, transparent 70%)", pointerEvents: "none" };
const heroContent = { padding: "32px 24px 28px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, textAlign: "center", position: "relative" };
const casinoIcon  = { fontSize: 64, lineHeight: 1, filter: "drop-shadow(0 0 20px rgba(74,222,128,0.5))" };
const heroH1      = { margin: 0, fontSize: 32, fontWeight: 900, color: "#4ade80", letterSpacing: "0.1em" };
const heroSub     = { margin: 0, fontSize: 14, color: "rgba(255,255,255,0.7)", lineHeight: 1.6 };
const featureRow  = { display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" };
const featureChip = { fontSize: 11, fontWeight: 800, padding: "5px 12px", borderRadius: 999, background: "rgba(255,255,255,0.1)", color: "#e2e8f0", border: `1px solid ${BORDER}` };
const errorBox    = { width: "100%", padding: "10px 14px", borderRadius: 12, background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", fontSize: 13, fontWeight: 700 };
const launchBtn   = { display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width: "100%", height: 56, border: 0, borderRadius: 16, background: `linear-gradient(135deg, ${GREEN}, #15803d)`, color: "#fff", fontSize: 17, fontWeight: 900, cursor: "pointer", letterSpacing: "0.04em", boxShadow: "0 8px 28px rgba(22,163,74,0.45)", marginTop: 4 };
const launchBtnDisabled = { ...launchBtn, background: "#1e2a1e", color: "#475569", boxShadow: "none", cursor: "not-allowed" };
const reelBtn     = { display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width: "100%", height: 56, border: 0, borderRadius: 16, background: "linear-gradient(135deg, #7c3aed, #5b21b6)", color: "#fff", fontSize: 17, fontWeight: 900, cursor: "pointer", letterSpacing: "0.04em", boxShadow: "0 8px 28px rgba(124,58,237,0.4)" };
const reelBtnDisabled = { ...reelBtn, background: "#1e1a2e", color: "#475569", boxShadow: "none", cursor: "not-allowed" };
const disclaimer  = { margin: 0, fontSize: 11, color: "rgba(255,255,255,0.35)", lineHeight: 1.5 };

const iframeWrap  = { position: "fixed", inset: 0, zIndex: 9999, display: "flex", flexDirection: "column", background: "#000", touchAction: "none" };
const iframeBar   = { height: 46, background: "#0a140a", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", flexShrink: 0, borderBottom: "1px solid rgba(255,255,255,0.08)", touchAction: "auto" };
const iframeTitle = { fontSize: 15, fontWeight: 900, color: "#4ade80", letterSpacing: "0.08em" };
const iframeClose = { border: 0, background: "transparent", cursor: "pointer", display: "grid", placeItems: "center", padding: 6 };
const iframeEl    = { flex: 1, border: "none", width: "100%", minHeight: 0, touchAction: "auto", WebkitOverflowScrolling: "touch", display: "block" };

export default CasinoPage;
